import React, { useState, useEffect } from 'react';
import { Form, Input, Checkbox, Tooltip, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import FormCard from 'pages/NewUniProfile/components/FormCard';
import UsPhoneInput from 'components/form/USPhoneInput';
import FormValidationHelper from 'utils/validation';
import { IBasicInfoStep } from 'types/newUniprofile';
import { editBasicInfo } from 'api/newUniprofile';
import { isValidNpi } from 'utils/form';
import TitleTip from 'assets/channel/titleTip.jpg';
import { useSwitchedProvider } from '../../SwitchedProviderContext';
import commonS from '../../s.module.less';
import s from './s.module.less';

interface Props {
    initValue?: Partial<IBasicInfoStep> | null;
    onSave?: () => void;
}

const BasicInfoCard: React.FC<Props> = ({ initValue, onSave }) => {
    const { scheduleToken } = useSwitchedProvider();
    const [form] = Form.useForm();
    const [npiRequired, setNpiRequired] = useState(true);
    const [sameAsPersonalPhone, setSameAsPersonalPhone] = useState(false);
    const [sameAsLoginEmail, setSameAsLoginEmail] = useState(false);

    useEffect(() => {
        if (initValue) {
            form.setFieldsValue(initValue);
            setSameAsPersonalPhone(initValue.tel === initValue.practicePhone);
            setSameAsLoginEmail(initValue.email === initValue.practiceEmail);
            setNpiRequired(!initValue.npiNotRequired);
        }
    }, [initValue]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const res = await editBasicInfo(
                {
                    ...initValue,
                    ...values,
                } as IBasicInfoStep,
                scheduleToken,
            );
            if (res.error) {
                message.error(res.error);
            } else {
                onSave?.();
            }
        } catch (error) {
            console.error('Form validation failed:', error);
            throw error;
        }
    };

    const renderEditing = () => (
        <Form form={form} layout="vertical" className={s.form}>
            <div className={s.formRow}>
                <Form.Item
                    label={
                        <div className={s.formLabel}>
                            Title
                            <Tooltip
                                color="#FFFFFF"
                                overlayInnerStyle={{ padding: '20px', width: '400px' }}
                                title={
                                    <div className={s.tipText}>
                                        Title will appear before the name. <img src={TitleTip} />
                                    </div>
                                }
                            >
                                <QuestionCircleOutlined className={s.tooltipIcon} />
                            </Tooltip>
                        </div>
                    }
                    name="title"
                    rules={[{ max: 10, message: 'Exceeded maximum character length of 10' }]}
                    className="flex-1"
                >
                    <Input type="text" placeholder="Ex. Dr., Sr., Hon., Mr., Mrs., Ms." />
                </Form.Item>
                <div className="flex-1">
                    <Form.Item
                        name="npi"
                        label="NPI number"
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (npiRequired && !value) {
                                        return Promise.reject('NPI number is a required field');
                                    }
                                    if (npiRequired && !isValidNpi(value)) {
                                        return Promise.reject('Please enter a valid NPI number');
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <Input placeholder="Enter your NPI number" disabled={!npiRequired} maxLength={10} />
                    </Form.Item>
                    <Form.Item name="npiNotRequired" valuePropName="checked" style={{ marginTop: '-16px' }}>
                        <Checkbox
                            onChange={(e) => {
                                setNpiRequired(!e.target.checked);
                                form.setFields([{ name: 'npi', errors: [] }]);
                            }}
                        >
                            My practice doesn&apos;t require an NPI
                        </Checkbox>
                    </Form.Item>
                </div>
            </div>
            <div className={s.formRow}>
                <div className="flex-1">
                    <Form.Item
                        name="practicePhone"
                        label={
                            <span className={s.formLabel}>
                                Practice phone number
                                <Tooltip
                                    color="#FFFFFF"
                                    title={
                                        <div className={s.tipText}>
                                            This can be the same as your personal phone number.
                                        </div>
                                    }
                                >
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </span>
                        }
                        rules={[
                            { required: true, message: 'Practice phone number is a required field' },
                            FormValidationHelper.validateUSPhoneNumber('Please enter a 10-digit phone number'),
                        ]}
                    >
                        <UsPhoneInput disabled={sameAsPersonalPhone} />
                    </Form.Item>
                    <div style={{ marginTop: '-12px' }}>
                        <Checkbox
                            checked={sameAsPersonalPhone}
                            onChange={(e) => {
                                form.setFieldsValue({
                                    practicePhone: e.target.checked ? form.getFieldValue('tel') : '',
                                });
                                setSameAsPersonalPhone(e.target.checked);
                            }}
                        >
                            Same as personal phone number
                        </Checkbox>
                    </div>
                </div>
                <div className="flex-1">
                    <Form.Item
                        name="practiceEmail"
                        label={
                            <span className={s.formLabel}>
                                Practice email address
                                <Tooltip
                                    color="#FFFFFF"
                                    title={<div className={s.tipText}>This can be the same as your work email.</div>}
                                >
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </span>
                        }
                        rules={[
                            { required: true, message: 'Practice email address is a required field' },
                            { type: 'email', message: 'Please enter a valid email address' },
                        ]}
                    >
                        <Input type="text" disabled={sameAsLoginEmail} />
                    </Form.Item>
                    <div style={{ marginTop: '-12px' }}>
                        <Checkbox
                            checked={sameAsLoginEmail}
                            onChange={(e) => {
                                setSameAsLoginEmail(e.target.checked);
                                form.setFieldsValue({
                                    practiceEmail: e.target.checked ? form.getFieldValue('email') : '',
                                });
                            }}
                        >
                            Same as login email address
                        </Checkbox>
                    </div>
                </div>
            </div>
        </Form>
    );

    const renderReview = () => {
        if (!initValue) return <div>Not provided</div>;
        const { title, npi, practicePhone, practiceEmail } = initValue;
        return (
            <div>
                <div className={commonS.row}>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>Title</div>
                        <div className={commonS.value}>{title || '-'}</div>
                    </div>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>NPI number</div>
                        <div className={commonS.value}>{npi || '-'}</div>
                    </div>
                </div>
                <div className={commonS.row}>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>Practice phone number</div>
                        <div className={commonS.value}>{practicePhone || '-'}</div>
                    </div>
                    <div className={commonS.displayItem}>
                        <div className={commonS.label}>Practice email address</div>
                        <div className={commonS.value}>{practiceEmail || '-'}</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <FormCard type="review" title="Basic information" hasContent onSave={handleSave}>
            {(isEditing) => (isEditing ? renderEditing() : renderReview())}
        </FormCard>
    );
};

export default BasicInfoCard;
